import { Fragment, useMemo, useCallback } from "react";

import { useQuery } from "@apollo/client";
import { Radio } from "antd";
import dayjs from "dayjs";
import { useDispatch } from "react-redux";

import {
    disabledDateTime,
    disabledDate,
    overlaps,
    RVEnhancedDateTimePickerFormItem,
    VALIDATOR_NO_PAST_DATES,
    RVFormItem,
} from "@regal-voice/ui";
import { getScheduleBlockedDates } from "Services/marketing-api/journeys/queries";
import { getSegmentById } from "Services/marketing-api/segments/queries";
import { setFields } from "Services/state/journeys/JourneyStateSlice";
import {
    SCHEDULED_JOURNEY_MAX_EXECUTIONS_PER_MINUTE,
    SCHEDULED_JOURNEY_MAX_SEGMENT_SIZE,
} from "Utils/JourneysConstants";

import WarningBanner from "../../../../../../shared/Components/WarningBanner/WarningBanner";
import { unflattenFields } from "../../../Utils/utils";

import styles from "../TriggerFormItems.module.scss";

// ENUM CLEAN UP
// eslint-disable-next-line no-restricted-syntax
export enum ScheduledJourneyFrequency {
    Once = "once",
}

export function ScheduledJourney({
    formValues,
    segmentUploading,
}: {
    formValues: Record<string, any>;
    segmentUploading: boolean;
}): JSX.Element {
    const dispatch = useDispatch();

    const { data: { getScheduleBlockedDates: blockedDates } = { getScheduleBlockedDates: [] } } = useQuery(
        getScheduleBlockedDates,
        {
            fetchPolicy: "cache-and-network",
        }
    );

    const { data } = useQuery(getSegmentById, {
        variables: { id: formValues.segmentId },
        skip: !formValues.segmentId,
        errorPolicy: "all",
        fetchPolicy: "network-only",
    });
    const segment = data?.getSegment;

    const { scheduledJourneyStartDateTimezone } = formValues;

    const startDateHeader = useMemo(() => {
        const startDateAndTimeTitle = "Start Date & Time";
        if (!segmentUploading) {
            return <Fragment key={"start-date-time"}>{startDateAndTimeTitle}</Fragment>;
        }
        return (
            <div key={"start-date-time-processing"}>
                <span>{startDateAndTimeTitle}</span>
                <WarningBanner>
                    Segment upload is still in progress. Allow extra time before starting the journey.
                </WarningBanner>
            </div>
        );
    }, [segmentUploading]);

    const validateDatetimeAndSegment = useCallback(
        async (_rule: any, dateValue: string) => {
            let segmentSize = segment?.count;
            if (!segmentSize) {
                // eslint-disable-next-line no-console
                console.warn("Didn't have a segment size so defaulting to the max size.");
                segmentSize = SCHEDULED_JOURNEY_MAX_SEGMENT_SIZE;
            }
            const journeyTimeToProcess = segment
                ? Math.ceil(segmentSize / SCHEDULED_JOURNEY_MAX_EXECUTIONS_PER_MINUTE)
                : 0;
            const startDate = dayjs(dateValue).tz(scheduledJourneyStartDateTimezone);
            const endDate = startDate.add(journeyTimeToProcess, "minute");
            const isOverlaping = overlaps({
                blockedDates,
                startDate,
                endDate,
                scheduledJourneyStartDateTimezone,
            });
            if (isOverlaping) {
                return Promise.reject(
                    new Error("Segment size is too large for the selected time. Choose another time.")
                );
            }
            return Promise.resolve();
        },
        [blockedDates, scheduledJourneyStartDateTimezone, segment]
    );
    const onStartDateChange = useCallback(
        (dateVal: Date) => {
            dispatch(
                setFields(
                    unflattenFields({
                        ...formValues,
                        scheduledJourneyStartDate: dateVal,
                    })
                )
            );
        },
        [dispatch, formValues]
    );

    return (
        <>
            <RVEnhancedDateTimePickerFormItem
                name="scheduledJourneyStartDate"
                formItemHeader={startDateHeader}
                label={"Start Date (In Eastern Timezone)"}
                className={styles.formItemHalf}
                onChange={onStartDateChange}
                timezone={scheduledJourneyStartDateTimezone}
                useUTCValue={true}
                disabledDate={disabledDate}
                disabledTime={disabledDateTime(blockedDates, scheduledJourneyStartDateTimezone)}
                rules={[
                    {
                        required: true,
                        message: "Select a date",
                    },
                    {
                        validator: validateDatetimeAndSegment,
                    },
                    VALIDATOR_NO_PAST_DATES,
                ]}
                showFormLabel
            />
            <RVFormItem
                name="scheduledJourneyFrequency"
                label="Frequency"
                className={styles.formItem}
                required
                initialValue={ScheduledJourneyFrequency.Once}
            >
                <Radio.Group size="small">
                    <Radio
                        className={styles.radio}
                        value={ScheduledJourneyFrequency.Once}
                        checked={true}
                        defaultChecked={true}
                    >
                        One Time
                    </Radio>
                </Radio.Group>
            </RVFormItem>
        </>
    );
}
